// game/smoothing.ts — Pure mathematical functions for coordinate wrapping and position smoothing

export interface SmoothingOptions {
  current: { x: number; y: number; rotation?: number };
  target: { x: number; y: number; rotation?: number };
  world: { w: number; h: number };
  rotationMultiplier?: number;
}

export function wrapCoordinate(coord: number, worldSize: number) {
  while (coord < 0) coord += worldSize;
  while (coord >= worldSize) coord -= worldSize;
  return coord;
}

const TWO_PI = Math.PI * 2;

export function normalizeRotation(angle: number): number {
  return ((angle % TWO_PI) + TWO_PI) % TWO_PI;
}

export function smoothRotation(
  current: number,
  target: number,
  smoothingFactor: number,
): number {
  const normalizedCurrent = normalizeRotation(current);
  const normalizedTarget = normalizeRotation(target);

  let rotationDiff = normalizedTarget - normalizedCurrent;
  if (rotationDiff > Math.PI) rotationDiff -= TWO_PI;
  if (rotationDiff < -Math.PI) rotationDiff += TWO_PI;

  return normalizeRotation(normalizedCurrent + rotationDiff * smoothingFactor);
}

export function smoothPosition(options: SmoothingOptions): void {
  const { current, target, world, rotationMultiplier = 1.5 } = options;

  let dx = target.x - current.x;
  let dy = target.y - current.y;

  if (dx > world.w / 2) dx -= world.w;
  if (dx < -world.w / 2) dx += world.w;
  if (dy > world.h / 2) dy -= world.h;
  if (dy < -world.h / 2) dy += world.h;

  const distance = Math.sqrt(dx * dx + dy * dy);

  let smoothingFactor = 0.15;
  if (distance > 100) {
    smoothingFactor = 0.3;
  } else if (distance > 50) {
    smoothingFactor = 0.2;
  }

  current.x += dx * smoothingFactor;
  current.y += dy * smoothingFactor;
  current.x = wrapCoordinate(current.x, world.w);
  current.y = wrapCoordinate(current.y, world.h);

  if (current.rotation !== undefined && target.rotation !== undefined) {
    current.rotation = smoothRotation(
      current.rotation,
      target.rotation,
      smoothingFactor * rotationMultiplier,
    );
  }
}
